import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackAddedShareLinkPasscode } from "@air/analytics";
import { Eye as EyeIcon, EyeClosed as EyeClosedIcon } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { IconButton } from "@air/primitive-icon-button";
import { Input } from "@air/primitive-input";
import { Switch } from "@air/primitive-switch";
import classNames from "classnames";
import { Formik } from "formik";
import { useToggle } from "__barrel_optimize__?names=useToggle!=!react-use";
import * as Yup from "yup";
import { UpsellButton } from "~/components/UpsellButton";
import { PASSCODE_PROTECTION_INPUT, PASSCODE_PROTECTION_LABEL, PASSCODE_PROTECTION_SUBMIT_BUTTON, PASSCODE_PROTECTION_TOGGLE } from "~/constants/testIDs";
import { usePlanFeature } from "~/hooks/usePlanFeature";
import { PASSWORD_REQS_STRING } from "~/utils/Auth";
const SignUpValidationSchema = Yup.object().shape({
    passcode: Yup.string().required("Passcode is required").min(8, "Passcode must be at least 8 characters.").matches(/\d+/, "Passcode must include a number.").matches(/[a-z]/, "Passcode must include one lowercase letter.").matches(/[A-Z]/, "Passcode must include one uppercase letter.").default("")
});
export const SharePrivateContentPasscodeProtection = (param)=>{
    let { shortIdInfo, onChangePasscode } = param;
    const canUsePasscodeProtectedLinks = usePlanFeature("passcodeProtectedLinks");
    const [showPasscode, toggleShowPasscode] = useToggle(true);
    const [isShowingPasscodeForm, showPasscodeForm] = useToggle(!!shortIdInfo.passcode);
    const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();
    var _shortIdInfo_passcode;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-14 text-grey-12",
                        "data-testid": PASSCODE_PROTECTION_LABEL,
                        children: "Add passcode protection"
                    }),
                    !canUsePasscodeProtectedLinks ? /*#__PURE__*/ _jsx(UpsellButton, {
                        children: "Upgrade to Air Pro"
                    }) : /*#__PURE__*/ _jsx(Switch, {
                        checked: isShowingPasscodeForm,
                        "data-testid": PASSCODE_PROTECTION_TOGGLE,
                        id: "password-protect",
                        onClick: async ()=>{
                            if (shortIdInfo.passcode && isShowingPasscodeForm) {
                                // don't await on the update. update immediately
                                onChangePasscode(null);
                            }
                            showPasscodeForm(!isShowingPasscodeForm);
                        }
                    })
                ]
            }),
            canUsePasscodeProtectedLinks && isShowingPasscodeForm && /*#__PURE__*/ _jsx(Formik, {
                validationSchema: SignUpValidationSchema,
                initialValues: {
                    passcode: (_shortIdInfo_passcode = shortIdInfo.passcode) !== null && _shortIdInfo_passcode !== void 0 ? _shortIdInfo_passcode : ""
                },
                onSubmit: async (param, param1)=>{
                    let { passcode } = param, { setSubmitting, resetForm } = param1;
                    try {
                        setSubmitting(true);
                        trackAddedShareLinkPasscode({
                            shortId: shortIdInfo === null || shortIdInfo === void 0 ? void 0 : shortIdInfo.id
                        });
                        await onChangePasscode(passcode || null);
                        setSubmitting(false);
                        resetForm({
                            values: {
                                passcode
                            }
                        });
                    } catch (_error) {
                        setSubmitting(false);
                    }
                },
                children: (param)=>{
                    let { isSubmitting, handleSubmit, values, handleChange, dirty, isValid, errors, touched, handleBlur } = param;
                    return /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("form", {
                                className: "relative mt-3 flex items-center",
                                onSubmit: handleSubmit,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "relative w-full",
                                        children: [
                                            /*#__PURE__*/ _jsx(Input, {
                                                size: "large",
                                                value: values.passcode,
                                                "data-testid": PASSCODE_PROTECTION_INPUT,
                                                onChange: handleChange,
                                                type: showPasscode ? "text" : "password",
                                                name: "passcode",
                                                className: classNames("w-full pr-8", !isValid && dirty && "border-red-9"),
                                                placeholder: "Enter passcode",
                                                onBlur: handleBlur
                                            }),
                                            /*#__PURE__*/ _jsx(IconButton, {
                                                appearance: "ghost",
                                                className: "absolute right-1 top-1",
                                                color: "grey",
                                                label: "Show passcode",
                                                icon: showPasscode ? EyeIcon : EyeClosedIcon,
                                                onClick: toggleShowPasscode,
                                                size: "medium"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        appearance: "filled",
                                        className: "ml-3",
                                        color: "grey",
                                        "data-testid": PASSCODE_PROTECTION_SUBMIT_BUTTON,
                                        disabled: isSubmitting || !dirty || !isValid,
                                        isLoading: isSubmitting,
                                        size: "large",
                                        type: "submit",
                                        children: "Save"
                                    })
                                ]
                            }),
                            !!errors.passcode && touched.passcode ? /*#__PURE__*/ _jsx("p", {
                                className: "mb-0 text-12 text-red-9",
                                children: errors.passcode
                            }) : /*#__PURE__*/ _jsx("p", {
                                className: "mb-0 text-12 text-grey-10",
                                children: PASSWORD_REQS_STRING
                            })
                        ]
                    });
                }
            })
        ]
    });
};
